import { ref, computed } from 'vue';
import { flowsAPI } from 'dashboard/api/flows';
import { useAlert } from 'dashboard/composables';

export function useFlowBuilder(flowId = null) {
  const flow = ref({
    name: '',
    description: '',
    category: 'evangelism',
    language: 'en',
    is_active: false,
    steps: [],
  });
  const isSaving = ref(false);
  const isLoading = ref(false);

  const stepCount = computed(() => flow.value.steps.length);

  function normalizeStep(step) {
    return {
      id: step.id,
      step_number: step.step_number,
      message_text: step.message_text,
      step_type: step.step_type || 'message',
      delay_seconds: step.delay_seconds || 0,
      // _tempId is only for new (unsaved) steps; cleared once a real id exists
      options: (step.options || []).map(o => ({
        id: o.id,
        option_number: o.option_number,
        option_text: o.option_text,
        next_step_id: o.next_step_id,
        tag_to_apply: o.tag_to_apply || '',
        notify_agent: o.notify_agent || false,
        outcome_label: o.outcome_label || '',
      })),
    };
  }

  let tempCounter = 0;
  function makeTempId() {
    tempCounter += 1;
    return `t${tempCounter}`;
  }

  function isTempId(v) {
    return typeof v === 'string' && /^t\d+$/.test(v);
  }

  async function load(id) {
    isLoading.value = true;
    try {
      const { data } = await flowsAPI.get(id);
      flow.value = {
        ...data,
        steps: (data.steps || []).map(normalizeStep),
      };
    } catch {
      useAlert('Failed to load flow.');
    } finally {
      isLoading.value = false;
    }
  }

  function addStep() {
    flow.value.steps.push({
      tempKey: makeTempId(),
      step_number: flow.value.steps.length + 1,
      message_text: '',
      step_type: 'message',
      delay_seconds: 0,
      options: [],
    });
  }

  function removeStep(index) {
    flow.value.steps.splice(index, 1);
    // Renumber
    flow.value.steps.forEach((s, i) => {
      s.step_number = i + 1;
    });
  }

  function moveStep(index, direction) {
    const steps = flow.value.steps;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
    steps.forEach((s, i) => {
      s.step_number = i + 1;
    });
  }

  function addOption(stepIndex) {
    const step = flow.value.steps[stepIndex];
    step.options.push({
      option_number: step.options.length + 1,
      option_text: '',
      next_step_id: null,
      tag_to_apply: '',
      notify_agent: false,
      outcome_label: '',
    });
  }

  function removeOption(stepIndex, optIndex) {
    flow.value.steps[stepIndex].options.splice(optIndex, 1);
    flow.value.steps[stepIndex].options.forEach((o, i) => {
      o.option_number = i + 1;
    });
  }

  function buildPayload(steps, tempToId = {}) {
    const payload = {
      ...flow.value,
      flow_steps_attributes: steps.map(step => ({
        ...step,
        tempKey: undefined,
        flow_options_attributes: step.options.map(opt => ({
          ...opt,
          // Resolve temp IDs to real DB IDs; null means "end flow"
          next_step_id: isTempId(opt.next_step_id)
            ? (tempToId[opt.next_step_id] ?? null)
            : opt.next_step_id,
        })),
      })),
    };
    delete payload.steps;
    return payload;
  }

  async function save() {
    isSaving.value = true;
    try {
      const steps = flow.value.steps;

      // Collect options that reference unsaved (temp) steps — need a second pass
      const pendingRefs = [];
      steps.forEach((step, si) => {
        step.options.forEach((opt, oi) => {
          if (isTempId(opt.next_step_id)) {
            pendingRefs.push({ si, oi, tempId: opt.next_step_id });
          }
        });
      });

      // Phase 1: save with temp refs nulled out (tempToId is empty)
      const doSave = payload =>
        flowId || flow.value.id
          ? flowsAPI.update(flow.value.id || flowId, payload)
          : flowsAPI.create(payload);

      const { data } = await doSave(buildPayload(steps));
      flow.value = { ...data, steps: (data.steps || []).map(normalizeStep) };

      // Phase 2: if any options pointed to new steps, resolve now that we have real IDs
      if (pendingRefs.length > 0) {
        // Build tempId → real DB id from _tempId stored on each original step
        const tempToId = {};
        steps.forEach((origStep, si) => {
          if (origStep.tempKey) {
            const savedStep = flow.value.steps[si];
            if (savedStep?.id) tempToId[origStep.tempKey] = savedStep.id;
          }
        });

        // Patch option next_step_ids in place
        pendingRefs.forEach(({ si, oi, tempId: tid }) => {
          flow.value.steps[si].options[oi].next_step_id = tempToId[tid] ?? null;
        });

        const { data: data2 } = await flowsAPI.update(
          flow.value.id,
          buildPayload(flow.value.steps)
        );
        flow.value = {
          ...data2,
          steps: (data2.steps || []).map(normalizeStep),
        };
      }

      useAlert('Flow saved successfully!');
      return flow.value;
    } catch (e) {
      useAlert(e?.response?.data?.error || 'Failed to save flow.');
      return null;
    } finally {
      isSaving.value = false;
    }
  }

  if (flowId) load(flowId);

  return {
    flow,
    isSaving,
    isLoading,
    stepCount,
    load,
    addStep,
    removeStep,
    moveStep,
    addOption,
    removeOption,
    save,
  };
}
